import React from 'react';
import { Box, CircularProgress, Stack } from '@mui/material';
import { AuthPermissions } from 'skipify-types';

import SendPaylinkForm from '../SendPaylinkForm';
import { useGetConfigsQuery, useGetPublicMerchantQuery } from '../../../store/modules/merchant/api';
import { PartialPaymentValue } from '../../../store/modules/merchant/types';
import { isPartialPaymentEnabled } from '../../../util/partialPaymentConfig';
import RecentPaylinksList from '../RecentPaylinksList';
import { usePaylinkSubmit } from '../hooks';
import CanAccess from '../../../common/CanAccess';
import { useMerchant } from '../../../contexts/MerchantContext';

const SendPaylinks = (): JSX.Element => {
  const { merchantId, merchantName: name } = useMerchant();
  const { data: publicData, isLoading: isLoadingPublicData } = useGetPublicMerchantQuery(
    merchantId,
    { skip: !merchantId },
  );
  const { data: configs } = useGetConfigsQuery(merchantId, {
    skip: !merchantId,
  });
  const partialPaymentConfig = configs?.find((c) => c.name === 'PARTIAL_PAYMENTS') as
    { value?: PartialPaymentValue } | undefined;
  const merchantName = publicData?.branding?.displayName ?? name;
  const currency = publicData?.acceptedCurrency;

  const {
    onPaylinkSubmit,
    isLoading: isLoadingSubmit,
    serverValidationError,
  } = usePaylinkSubmit({ merchantId });

  if (!merchantId || isLoadingPublicData) {
    return (
      <Box sx={{ mt: 10, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: 'black' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ my: 5 }}>
      <Stack
        sx={{
          flexDirection: { xs: 'column', sm: 'column', md: 'row' },
          gap: {
            xs: 7,
            sm: 7,
            md: 7,
            lg: 10,
          },
        }}
      >
        <CanAccess
          permissions={[AuthPermissions.CREATE_PAYLINKS, AuthPermissions.CREATE_OWN_PAYLINKS]}
        >
          <Box sx={{ width: { xs: '100%', sm: '100%', md: '54%' } }}>
            <SendPaylinkForm
              merchantName={merchantName}
              merchantId={merchantId}
              onSubmit={onPaylinkSubmit}
              isLoadingSubmit={isLoadingSubmit}
              serverValidationError={serverValidationError}
              currency={currency}
              isRecurringCheckboxEnabled={publicData?.paylinksRecurringCheckbox}
              isPartialPaymentCheckboxEnabled={isPartialPaymentEnabled(partialPaymentConfig)}
            />
          </Box>
        </CanAccess>
        <CanAccess permissions={[AuthPermissions.READ_PAYLINKS, AuthPermissions.READ_OWN_PAYLINKS]}>
          <Box sx={{ width: { xs: '100%', sm: '100%', md: '46%' } }}>
            <RecentPaylinksList merchantName={merchantName} merchantId={merchantId} />
          </Box>
        </CanAccess>
      </Stack>
    </Box>
  );
};

export default SendPaylinks;
